import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getAirArabiaPricing } from "../Service/AirarabiaApi";
import { formatNumber } from "../../../utils/convertToNumber";
import FlightPricingItemAirArabia from "./FlightPricingItem.airarabia";
import Loading from "../../Loading";
import {
  CheckCircle,
  Close,
  FlightLand,
  FlightTakeoff,
  Luggage,
  Restaurant,
  Shield,
  Star,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  USER_ROLE_EMPLOYEE,
  USER_ROLE_SUPERAGENT,
} from "../../../common/constants";
import { useSelector } from "react-redux";

const FlightPricingAirArabia = () => {
  const {
    segmentData,
    queryURL,
    bookingDetails,
    cabin,
    charges,
    originalPath,
    passengers: passengersData,
    currency,
  } = useLocation().state;

  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);

  const [price, setPrice] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [selectedOutboundBundle, setSelectedOutboundBundle] = useState(null);
  const [selectedInboundBundle, setSelectedInboundBundle] = useState(null);
  const [loading, setLoading] = useState(false);

  const BASIC_BUNDLE_OUTBOUND = {
    id: "basic",
    name: "Basic",
    price: 0,
    applicableOndSequence: "0",
  };
  const BASIC_BUNDLE_INBOUND = {
    id: "basic",
    name: "Basic",
    price: 0,
    applicableOndSequence: "1",
  };
  const BASIC_BUNDLE_SINGLE = { id: "basic", name: "Basic", price: 0 };

  useEffect(() => {
    const fetchAirArabiaPricing = async () => {
      const response = await getAirArabiaPricing(segmentData, setLoading);
      setPrice(response?.data);
    };
    fetchAirArabiaPricing();
  }, [segmentData]);

  const isSuper = [USER_ROLE_SUPERAGENT, USER_ROLE_EMPLOYEE].includes(
    user?.role,
  );

  useEffect(() => {
    if (!isSuper && charges <= 0) {
      toast.error("Network Error, Please try again");
      navigate(-1);
    }
  }, [charges, isSuper, navigate]);

  const handleBookFlight = () => {
    const isRealBundle = (b) => b && b.id !== "basic";

    const hasRealBundle =
      isRealBundle(selectedBundle) ||
      isRealBundle(selectedOutboundBundle) ||
      isRealBundle(selectedInboundBundle);

    if (originalPath?.length > 1) {
      if (!selectedOutboundBundle || !selectedInboundBundle) {
        toast.error(
          "Please select a flight bundle for outbound and inbound flights",
        );
        return;
      }
    } else {
      if (!selectedBundle) {
        toast.error("Please select a flight bundle for the flight");
        return;
      }
    }

    const state = {
      passengersData,
      originalPath,
      response: price,
      selectedBundle: isRealBundle(selectedBundle) ? selectedBundle : null,
      selectedOutboundBundle: isRealBundle(selectedOutboundBundle)
        ? selectedOutboundBundle
        : null,
      selectedInboundBundle: isRealBundle(selectedInboundBundle)
        ? selectedInboundBundle
        : null,
      passengers: expandedPassengers,
      pricingRequest: segmentData,
      queryURL,
      charges: charges || 0,
      currency,
    };

    if (hasRealBundle) {
      navigate(`/price-with-bundle${queryURL}`, {
        state: {
          ...state,
          type: "bundle",
        },
      });
    } else {
      navigate(`/basic-flight-pricing${queryURL}`, {
        state: {
          ...state,
          sessionInfo: price?.sessionInfo,
          type: "ancillaries",
        },
      });
    }
  };

  const expandedPassengers = [];
  const passengers = price?.pricing?.passengers || [];

  passengers.forEach((passengerGroup) => {
    if (passengerGroup.passengerType?.code === "INF") {
      return;
    }

    const quantity = passengerGroup.passengerType?.quantity || 1;
    const travelerRefNumbers = passengerGroup.travelerRefNumbers || [];

    for (let i = 0; i < quantity; i++) {
      expandedPassengers.push({
        passengerType: passengerGroup.passengerType?.code,
        refNumber:
          travelerRefNumbers[i] ||
          `${passengerGroup.passengerType?.code}${i + 1}`,
      });
    }
  });

  const totalTax = price?.pricing?.passengers
    ?.reduce((acc, item) => {
      const taxSum =
        item.taxes?.reduce((taxAcc, tax) => taxAcc + Number(tax.amount), 0) ||
        0;
      const quantity = item.passengerType?.quantity || 1;
      return Number(acc + taxSum * quantity);
    }, 0)
    .toFixed(2);

  const processItinerary = (segments, searchParams) => {
    if (!segments?.length || !searchParams?.length) {
      return { outbound: segments || [], inbound: [] };
    }

    const outboundDest = searchParams[0].Destination;

    const splitIndex = segments.findIndex(
      (seg) => seg.arrivalAirport.code === outboundDest,
    );

    if (splitIndex === -1) {
      return { outbound: segments, inbound: [] };
    }

    const outbound = segments.slice(0, splitIndex + 1);
    const inbound =
      searchParams.length > 1 ? segments.slice(splitIndex + 1) : [];

    return { outbound, inbound };
  };

  const { outbound, inbound } = processItinerary(price?.flights, originalPath);

  const formatCurrency = (amount, currency) => {
    return `${currency} ${amount?.toFixed(2)}`;
  };

  const clearSelectedBundle = () => {
    setSelectedBundle(null);
    setSelectedOutboundBundle(null);
    setSelectedInboundBundle(null);
  };

  const handleBundleSelection = (bundle) => {
    if (originalPath?.length > 1) {
      if (bundle.applicableOndSequence === "0") {
        setSelectedOutboundBundle(bundle);
      } else if (bundle.applicableOndSequence === "1") {
        setSelectedInboundBundle(bundle);
      }
    } else {
      setSelectedBundle(bundle);
    }
  };

  const isBundleSelected = (bundle) => {
    if (originalPath?.length > 1) {
      if (bundle.applicableOndSequence === "0") {
        return selectedOutboundBundle?.id === bundle.id;
      } else if (bundle.applicableOndSequence === "1") {
        return selectedInboundBundle?.id === bundle.id;
      }
    }
    return selectedBundle?.id === bundle.id;
  };

  const totalPrice = () => {
    const total =
      price?.pricing?.totals?.totalFare?.amount +
      charges +
      (+selectedBundle?.price || 0) +
      (+selectedOutboundBundle?.price || 0) +
      (+selectedInboundBundle?.price || 0);
    return Number(total.toFixed(2));
  };

  if (loading) {
    return <Loading />;
  }

  const handBag = price?.bundles[0]?.description
    ?.split("\n")[0]
    ?.startsWith("Handbag:")
    ? price?.bundles[0]?.description?.split("\n")[0]
    : null;

  return (
    <div className="mt-4 sm:p-6 lg:mx-24 md:mt-8">
      <div className="row">
        <div className="col-md-9">
          <>
            {price &&
              [outbound, inbound]
                .filter((legs) => legs?.length > 0)
                .map((flights, index) => (
                  <FlightPricingItemAirArabia
                    key={index}
                    bookingDetails={bookingDetails || []}
                    cabin={cabin}
                    flights={flights}
                    charges={charges}
                  />
                ))}
          </>
          <>
            {price?.bundles && price?.bundles?.length > 0 && (
              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="flex items-center text-xl font-bold text-gray-900">
                    <Star className="mr-2 w-5 h-5 text-yellow-500" />
                    Choose Your Experience
                  </h3>
                  {(selectedBundle ||
                    selectedOutboundBundle ||
                    selectedInboundBundle) && (
                    <button
                      onClick={clearSelectedBundle}
                      className="flex items-center space-x-1 text-sm text-gray-600 transition-colors hover:text-red-600"
                    >
                      <Close className="w-4 h-4" />
                      <span>Clear All Selections</span>
                    </button>
                  )}
                </div>

                {/* Outbound Bundles */}
                {price?.bundles?.filter(
                  (bundle) => bundle.applicableOndSequence === "0",
                ).length > 0 && (
                  <div className="mb-8">
                    <h4 className="flex items-center mb-2 text-lg font-semibold text-gray-800">
                      <FlightTakeoff className="mr-2 w-4 h-4 text-main" />
                      Outbound Flight Bundles
                    </h4>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      {/* Basic Bundle Option */}
                      <div
                        className={`relative flex flex-col p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 h-full ${
                          (
                            originalPath?.length > 1
                              ? selectedOutboundBundle?.id === "basic"
                              : selectedBundle?.id === "basic"
                          )
                            ? "ring-1 shadow-md bg-amber-50/40 border-main-900 ring-main-900"
                            : "bg-white border-gray-100 hover:border-main-300 hover:shadow-lg"
                        }`}
                        onClick={() =>
                          originalPath?.length > 1
                            ? setSelectedOutboundBundle(BASIC_BUNDLE_OUTBOUND)
                            : setSelectedBundle(BASIC_BUNDLE_SINGLE)
                        }
                      >
                        {(originalPath?.length > 1
                          ? selectedOutboundBundle?.id === "basic"
                          : selectedBundle?.id === "basic") && (
                          <div className="absolute top-0 right-0 p-2 transform translate-x-1/3 -translate-y-1/3">
                            <div className="p-1 bg-white rounded-full shadow-sm">
                              <CheckCircle className="w-5 h-5 text-main-900" />
                            </div>
                          </div>
                        )}

                        <div className="mb-6">
                          <h5 className="mb-1 text-xl font-bold text-gray-900">
                            Basic
                          </h5>
                          <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Standard Fare
                          </p>
                        </div>

                        <div className="mb-6">
                          <div className="text-2xl font-black text-main-900">
                            + {currency || "ETB"} 0.00
                          </div>
                        </div>

                        <div className="mt-5 space-y-2">
                          {handBag && (
                            <div className="flex items-start text-sm text-gray-600">
                              <CheckCircle className="flex-shrink-0 mt-0.5 mr-2 w-4 h-4 text-green-500" />
                              <span className="leading-tight">{handBag}</span>
                            </div>
                          )}
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Baggage with additional fee.
                            </span>
                          </div>
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Meals with additional fee.
                            </span>
                          </div>
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Seats with additional fee.
                            </span>
                          </div>
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Beverages with additional fee.
                            </span>
                          </div>
                        </div>

                        <button
                          className={`px-4 py-2 mb-2 w-full text-white rounded-lg transition-colors mt-auto ${
                            (
                              originalPath?.length > 1
                                ? selectedOutboundBundle?.id === "basic"
                                : selectedBundle?.id === "basic"
                            )
                              ? "bg-main-900"
                              : "bg-main hover:bg-main-900"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            originalPath?.length > 1
                              ? setSelectedOutboundBundle(BASIC_BUNDLE_OUTBOUND)
                              : setSelectedBundle(BASIC_BUNDLE_SINGLE);
                          }}
                        >
                          {(
                            originalPath?.length > 1
                              ? selectedOutboundBundle?.id === "basic"
                              : selectedBundle?.id === "basic"
                          )
                            ? "Selected"
                            : "Select Basic"}
                        </button>
                      </div>

                      {price?.bundles
                        .filter(
                          (bundle) => bundle.applicableOndSequence === "0",
                        )
                        .map((bundle) => (
                          <div
                            key={bundle.id}
                            className={`relative flex flex-col px-4 py-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 h-full ${
                              isBundleSelected(bundle)
                                ? "ring-1 shadow-md bg-amber-50/40 border-main-900 ring-main-900"
                                : "bg-white border-gray-100 hover:border-main-300 hover:shadow-lg"
                            }`}
                            onClick={() => handleBundleSelection(bundle)}
                          >
                            {isBundleSelected(bundle) && (
                              <div className="absolute top-0 right-0 p-2 transform translate-x-1/3 -translate-y-1/3">
                                <div className="p-1 bg-white rounded-full shadow-sm">
                                  <CheckCircle className="w-5 h-5 text-main-900" />
                                </div>
                              </div>
                            )}

                            <div className="mb-6">
                              <h5 className="mb-1 text-xl font-bold text-gray-900">
                                {bundle.name}
                              </h5>
                              <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                                Class: {bundle.bookingClasses}
                              </p>
                            </div>

                            <div className="mb-6">
                              <div className="text-2xl font-black text-main-900">
                                +{formatCurrency(bundle.price, "ETB")}
                              </div>
                            </div>

                            {/* Included Services */}
                            {/* <div className="flex flex-wrap gap-2 mb-2">
                              {bundle.includedServices.map((service, index) => (
                                <div
                                  key={index}
                                  className="flex items-center px-2.5 py-1 text-[10px] font-bold rounded-lg text-main-700 bg-main-50 border border-main-100 uppercase tracking-tighter"
                                >
                                  <span className="mr-1">
                                    {getBundleIcon(service)}
                                  </span>
                                  {service.replace("_", " ")}
                                </div>
                              ))}
                            </div> */}

                            {/* Description */}
                            <div className="mt-auto space-y-2">
                              {bundle.description
                                .split("\n")
                                .filter((line) => line.trim())
                                .map((line, index) => (
                                  <div
                                    key={index}
                                    className="flex items-start text-sm text-gray-600"
                                  >
                                    <CheckCircle className="flex-shrink-0 mt-0.5 mr-2 w-4 h-4 text-green-500" />
                                    <span className="leading-tight">
                                      {line}
                                    </span>
                                  </div>
                                ))}
                            </div>
                            {/* add button to select the bundle */}
                            <button
                              className={`px-4 py-2 mt-4 w-full text-white rounded-lg transition-colors ${
                                isBundleSelected(bundle)
                                  ? "bg-main-900"
                                  : "bg-main hover:bg-main-900"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBundleSelection(bundle);
                              }}
                            >
                              {isBundleSelected(bundle)
                                ? "Selected"
                                : "Select Bundle"}
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Inbound Bundles */}
                {price.bundles.filter(
                  (bundle) => bundle.applicableOndSequence === "1",
                ).length > 0 && (
                  <div>
                    <h4 className="flex items-center mb-4 text-lg font-semibold text-gray-800">
                      <FlightLand className="mr-2 w-4 h-4 text-green-600 transform" />
                      Inbound Flight Bundles
                    </h4>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      {/* Basic Bundle Option (Inbound) */}
                      <div
                        className={`relative flex flex-col p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 h-full ${
                          selectedInboundBundle?.id === "basic"
                            ? "border-green-600 ring-1 ring-green-600 shadow-md bg-green-50/40"
                            : "bg-white border-gray-100 hover:border-green-300 hover:shadow-lg"
                        }`}
                        onClick={() =>
                          setSelectedInboundBundle(BASIC_BUNDLE_INBOUND)
                        }
                      >
                        {selectedInboundBundle?.id === "basic" && (
                          <div className="absolute top-0 right-0 p-2 transform translate-x-1/3 -translate-y-1/3">
                            <div className="p-1 bg-white rounded-full shadow-sm">
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                          </div>
                        )}

                        <div className="mb-6">
                          <h5 className="mb-1 text-xl font-bold text-gray-900">
                            Basic
                          </h5>
                          <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Standard Fare
                          </p>
                        </div>

                        <div className="mb-6">
                          <div className="text-2xl font-black text-green-700">
                            + {currency || "ETB"} 0.00
                          </div>
                        </div>

                        <div className="mt-5 space-y-2">
                          {handBag && (
                            <div className="flex items-start text-sm text-gray-600">
                              <CheckCircle className="flex-shrink-0 mt-0.5 mr-2 w-4 h-4 text-green-500" />
                              <span className="leading-tight">{handBag}</span>
                            </div>
                          )}
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Baggage with additional fee.
                            </span>
                          </div>
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Meals with additional fee.
                            </span>
                          </div>
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Seats with additional fee.
                            </span>
                          </div>
                          <div className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-lg font-bold text-red-600">
                              $
                            </span>
                            <span className="mt-2 leading-tight">
                              Beverages with additional fee.
                            </span>
                          </div>
                        </div>

                        <button
                          className={`px-4 py-2 mt-auto mb-2 w-full text-white rounded-lg transition-colors ${
                            selectedInboundBundle?.id === "basic"
                              ? "bg-green-600"
                              : "bg-green-500 hover:bg-green-600"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInboundBundle(BASIC_BUNDLE_INBOUND);
                          }}
                        >
                          {selectedInboundBundle?.id === "basic"
                            ? "Selected"
                            : "Select Basic"}
                        </button>
                      </div>

                      {price.bundles
                        .filter(
                          (bundle) => bundle.applicableOndSequence === "1",
                        )
                        .map((bundle) => (
                          <div
                            key={bundle.id}
                            className={`relative flex flex-col px-4 py-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 h-full ${
                              isBundleSelected(bundle)
                                ? "border-green-600 ring-1 ring-green-600 shadow-md bg-green-50/40"
                                : "bg-white border-gray-100 hover:border-green-300 hover:shadow-lg"
                            }`}
                            onClick={() => handleBundleSelection(bundle)}
                          >
                            {isBundleSelected(bundle) && (
                              <div className="absolute top-0 right-0 p-2 transform translate-x-1/3 -translate-y-1/3">
                                <div className="p-1 bg-white rounded-full shadow-sm">
                                  <CheckCircle className="w-6 h-6 text-green-600" />
                                </div>
                              </div>
                            )}
                            <div className="mb-6">
                              <h5 className="mb-1 text-xl font-bold text-gray-900">
                                {bundle.name}
                              </h5>
                              <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                                Class: {bundle.bookingClasses}
                              </p>
                            </div>
                            <div className="mb-6">
                              <div className="text-2xl font-black text-green-700">
                                +{formatCurrency(bundle.price, "ETB")}
                              </div>
                            </div>
                            {/* Included Services
                            <div className="flex flex-wrap gap-2 mb-6">
                              {bundle.includedServices.map((service, index) => (
                                <div
                                  key={index}
                                  className="flex items-center px-2.5 py-1 text-[10px] font-bold rounded-lg text-green-700 bg-green-50 border border-green-100 uppercase tracking-tighter"
                                >
                                  <span className="mr-1">
                                    {getBundleIcon(service)}
                                  </span>
                                  {service.replace("_", " ")}
                                </div>
                              ))}
                            </div> */}
                            {/* Description */}
                            <div className="mt-auto space-y-2">
                              {bundle.description
                                .split("\n")
                                .filter((line) => line.trim())
                                .map((line, index) => (
                                  <div
                                    key={index}
                                    className="flex items-start text-sm text-gray-600"
                                  >
                                    <CheckCircle className="flex-shrink-0 mt-0.5 mr-2 w-4 h-4 text-green-500" />
                                    <span className="leading-tight">
                                      {line}
                                    </span>
                                  </div>
                                ))}
                            </div>
                            {/* add button to select the bundle */}
                            <button
                              className={`px-4 py-2 mt-4 w-full text-white rounded-lg transition-colors ${
                                isBundleSelected(bundle)
                                  ? "bg-green-600"
                                  : "bg-green-500 hover:bg-green-600"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBundleSelection(bundle);
                              }}
                            >
                              {isBundleSelected(bundle)
                                ? "Selected"
                                : "Select Bundle"}
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        </div>
        <div className="col-md-3 h-fit">
          <div className="shadow-lg card">
            <div className="p-3 text-center border-b border-solid border-x-gray-300">
              <div className="row">
                <div className="col-md-6">
                  <h3 className="card-title">Flight Cost</h3>
                </div>
                <div className="col-md-6">
                  <h3 className="card-title">
                    {price?.pricing.totals.totalFare?.currency}{" "}
                    {/* {formatNumber(baseFare)} */}
                    {formatNumber(
                      Number(
                        Number(price?.pricing?.totals?.totalFare?.amount),
                      ).toFixed(2),
                    )}
                  </h3>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="flex flex-col justify-between mx-3 space-y-1 text-black">
                <div className="flex justify-between pb-1 space-y-1">
                  <div className="">
                    <p>Base Fare </p>
                  </div>
                  <div className="">
                    <p>
                      {price?.pricing?.totals?.totalFare?.currency}{" "}
                      {formatNumber(
                        price?.pricing?.totals?.baseFare?.amount,
                      )}{" "}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between pb-1 space-y-1">
                  <div className="">
                    <p>Taxes </p>
                  </div>
                  <div className="">
                    <p>
                      {price?.pricing?.totals?.totalFare?.currency}{" "}
                      {formatNumber(totalTax)}
                    </p>
                  </div>
                </div>
                <hr />

                {price?.pricing?.passengers?.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2"
                    >
                      <p>
                        {item.passengerType?.quantity} *{" "}
                        {item.passengerType?.code}
                      </p>
                      <p className="">
                        {item.totalFare.currency}{" "}
                        {formatNumber(
                          item.totalFare.amount * item.passengerType?.quantity,
                        )}
                      </p>
                    </div>
                  );
                })}

                <hr />
                <div className="flex justify-between mt-2 space-y-2">
                  <p>Service Charge </p>
                  <p>
                    {price?.pricing?.totals?.totalFare?.currency}{" "}
                    {formatNumber(charges)}
                  </p>
                </div>
                <hr />

                {/* Bundle Prices */}
                {selectedBundle && selectedBundle.price > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {selectedBundle.name} Bundle
                    </span>
                    <span className="font-medium">
                      {formatCurrency(selectedBundle.price, "ETB")}
                    </span>
                  </div>
                )}

                {selectedOutboundBundle && selectedOutboundBundle.price > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Outbound: {selectedOutboundBundle.name}
                    </span>
                    <span className="font-medium">
                      {formatCurrency(selectedOutboundBundle.price, "ETB")}
                    </span>
                  </div>
                )}

                {selectedInboundBundle && selectedInboundBundle.price > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Inbound: {selectedInboundBundle.name}
                    </span>
                    <span className="font-medium">
                      {formatCurrency(selectedInboundBundle.price, "ETB")}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="card-footer">
              <div className="justify-end d-flex">
                <div className="text-xl font-bold text-black">
                  Total Price: {price?.pricing?.totals?.totalFare?.currency}{" "}
                  {formatNumber(totalPrice())}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-8 col-md-9">
          <div className="justify-end d-flex">
            <button
              className="rounded-lg btn btn-primary"
              // onClick={handleClick}
              onClick={handleBookFlight}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightPricingAirArabia;
